import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMe } from "../api/hooks/useMe";
import { useMembers } from "../api/hooks/useMembers";
import { useInvites } from "../api/hooks/useInvites";
import { useUpdateMemberRole } from "./useUpdateMemberRole";
import { useRemoveMember } from "./useRemoveMember";
import { useRevokeInvite } from "./useRevokeInvite";
import { useWorkspace } from "../api/hooks/useWorkspace";
import { Tooltip } from "@archlang/ui";
import { InviteModal } from "./InviteModal";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { Table, THead, TBody, Tr, Th, Td } from "../ui/Table";
import { useContextMenu, type ContextMenuItem } from "../ui/ContextMenu";
import { SkeletonRows } from "../ui/Skeleton";
import { useToast } from "../ui/Toast";
import { Avatar } from "../ui/Avatar";
import { PageHeader } from "../ui/PageHeader";
import { useTranslation, apiErrorMessage } from "../i18n";
import type { Member } from "../api/types";

type Role = "owner" | "member" | "viewer";

// ── Role select ───────────────────────────────────────────────────────────────

interface RoleSelectProps {
  readonly value: Role;
  readonly disabled?: boolean;
  readonly title?: string;
  readonly onChange: (role: Role) => void;
}

function RoleSelect({ value, disabled, title, onChange }: RoleSelectProps) {
  const { t } = useTranslation("workspace");
  return (
    <select
      value={value}
      disabled={disabled}
      title={title}
      onChange={(e) => onChange(e.target.value as Role)}
      className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-sm text-slate-100
        focus:outline-none focus:ring-2 focus:ring-arch-500 focus:ring-offset-2 focus:ring-offset-slate-950
        disabled:cursor-not-allowed disabled:opacity-50"
    >
      <option value="viewer">{t("members.roleOptions.viewer")}</option>
      <option value="member">{t("members.roleOptions.member")}</option>
      <option value="owner">{t("members.roleOptions.owner")}</option>
    </select>
  );
}

// ── Members table ─────────────────────────────────────────────────────────────

interface MembersTableProps {
  readonly slug: string;
  readonly members: Member[];
  readonly myUserId: string;
  readonly isOwner: boolean;
}

function MembersTable({ slug, members, myUserId, isOwner }: MembersTableProps) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const toast = useToast();
  const updateRole = useUpdateMemberRole(slug);
  const remove = useRemoveMember(slug);
  const menu = useContextMenu();

  const ownerCount = members.filter((m) => m.role === "owner").length;
  const isSoleOwner = (m: Member) =>
    m.userId === myUserId && m.role === "owner" && ownerCount === 1;

  function handleRoleChange(member: Member, newRole: Role) {
    updateRole.mutate(
      { userId: member.userId, role: newRole },
      {
        onSuccess: () =>
          toast.success(
            t("members.roleChanged", {
              name: member.displayName,
              role: t(`members.roles.${newRole}`),
            }),
          ),
        onError: (e) => toast.error(apiErrorMessage(e, tErr)),
      },
    );
  }

  function handleRemove(member: Member) {
    remove.mutate(member.userId, {
      onSuccess: () => toast.success(t("members.removed", { name: member.displayName })),
      onError: (e) => toast.error(apiErrorMessage(e, tErr)),
    });
  }

  function menuItems(member: Member): ContextMenuItem[] {
    const items: ContextMenuItem[] = [
      { label: t("members.copyEmail"), onClick: () => void navigator.clipboard?.writeText(member.email) },
    ];
    // Remove is owner-only and never offered for the sole owner (mirrors the row button).
    if (isOwner && !isSoleOwner(member)) {
      items.push({
        label: t("members.remove"),
        onClick: () => handleRemove(member),
        danger: true,
        separator: true,
        disabled: remove.isPending || updateRole.isPending,
      });
    }
    return items;
  }

  if (members.length === 0) {
    return (
      <p className="text-sm text-slate-400">{t("members.empty")}</p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-700">
      {menu.node}
      <Table>
        <THead>
          <Tr>
            <Th>{t("members.colMember")}</Th>
            <Th>{t("members.colEmail")}</Th>
            <Th>{t("members.colRole")}</Th>
            {isOwner ? <Th className="w-20" /> : null}
          </Tr>
        </THead>
        <TBody>
          {members.map((m) => {
            const sole = isSoleOwner(m);
            return (
              <Tr key={m.userId} onContextMenu={(e) => menu.open(e, menuItems(m))}>
                <Td>
                  <div className="flex items-center gap-2.5">
                    <Avatar name={m.displayName} size="lg" />
                    <span className="font-medium text-slate-100">{m.displayName}</span>
                  </div>
                </Td>
                <Td className="text-slate-400">{m.email}</Td>
                <Td>
                  {isOwner ? (
                    <RoleSelect
                      value={m.role as Role}
                      disabled={sole || updateRole.isPending || remove.isPending}
                      title={sole ? t("members.soleOwnerRoleTooltip") : undefined}
                      onChange={(newRole) => handleRoleChange(m, newRole)}
                    />
                  ) : (
                    <span className="capitalize text-slate-300">{t(`members.roles.${m.role}`)}</span>
                  )}
                </Td>
                {isOwner ? (
                  <Td className="text-right">
                    <Tooltip content={sole ? t("members.soleOwnerRemoveTooltip") : undefined}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={sole || remove.isPending || updateRole.isPending}
                        onClick={() => handleRemove(m)}
                      >
                        {t("members.remove")}
                      </Button>
                    </Tooltip>
                  </Td>
                ) : null}
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}

// ── Pending invites ───────────────────────────────────────────────────────────

function PendingInvites({ slug, isOwner }: { readonly slug: string; readonly isOwner: boolean }) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const toast = useToast();
  const invites = useInvites(slug);
  const revoke = useRevokeInvite(slug);
  const menu = useContextMenu();

  const [copiedId, setCopiedId] = useState<string | null>(null);

  async function copyUrl(token: string, id: string) {
    const url = `${window.location.origin}/invite/${token}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      try {
        const el = document.createElement("textarea");
        el.value = url;
        el.style.position = "fixed";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      } catch {
        /* silent fail */
      }
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  }

  function revokeInvite(id: string) {
    revoke.mutate(id, {
      onSuccess: () => toast.success(t("members.invites.revoked")),
      onError: (e) => toast.error(apiErrorMessage(e, tErr)),
    });
  }

  function menuItems(inv: { id: string; token: string }): ContextMenuItem[] {
    return [
      { label: t("members.invites.copyLink"), onClick: () => void copyUrl(inv.token, inv.id) },
      {
        label: t("members.invites.revoke"),
        onClick: () => revokeInvite(inv.id),
        danger: true,
        separator: true,
        disabled: revoke.isPending,
      },
    ];
  }

  // Only show section if the user is an owner (invite management is owner-only)
  if (!isOwner) return null;

  if (invites.isLoading) {
    return (
      <section className="mt-8">
        <h2 className="mb-3 text-base font-semibold text-slate-200">{t("members.invites.title")}</h2>
        <span className="sr-only">{t("members.invites.loading")}</span>
        <SkeletonRows rows={2} />
      </section>
    );
  }

  if (invites.isError) {
    // The list endpoint IS implemented, so a real error must be shown, not
    // swallowed — otherwise an owner can't tell "no invites" from "load failed"
    // (bugloop L1 #4).
    return (
      <section className="mt-8">
        <h2 className="mb-3 text-base font-semibold text-slate-200">{t("members.invites.title")}</h2>
        <Banner kind="error">{t("members.invites.loadFailed", { message: invites.error ? apiErrorMessage(invites.error, tErr) : "" })}</Banner>
      </section>
    );
  }

  const pending = (invites.data ?? []).filter((inv) => inv.acceptedAt === null);

  if (pending.length === 0) {
    return (
      <section className="mt-8">
        <h2 className="mb-3 text-base font-semibold text-slate-200">{t("members.invites.title")}</h2>
        <p className="text-sm text-slate-400">{t("members.invites.empty")}</p>
      </section>
    );
  }

  return (
    <section className="mt-8">
      <h2 className="mb-3 text-base font-semibold text-slate-200">{t("members.invites.title")}</h2>
      {menu.node}
      <div className="overflow-x-auto rounded-lg border border-slate-700">
        <Table>
          <THead>
            <Tr>
              <Th>{t("members.invites.colRole")}</Th>
              <Th>{t("members.invites.colExpires")}</Th>
              <Th className="w-40" />
            </Tr>
          </THead>
          <TBody>
            {pending.map((inv) => (
              <Tr key={inv.id} onContextMenu={(e) => menu.open(e, menuItems(inv))}>
                <Td className="capitalize">{t(`members.roles.${inv.role}`)}</Td>
                <Td className="text-slate-400">
                  {inv.expiresAt
                    ? new Date(inv.expiresAt).toLocaleDateString()
                    : t("members.invites.never")}
                </Td>
                <Td className="text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyUrl(inv.token, inv.id)}
                    >
                      {copiedId === inv.id ? t("members.invites.copied") : t("members.invites.copyLink")}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => revokeInvite(inv.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      {t("members.invites.revoke")}
                    </Button>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function MembersPage() {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const { slug } = useParams<{ slug: string }>();
  const [inviteOpen, setInviteOpen] = useState(false);

  const me = useMe();
  const ws = useWorkspace(slug ?? "");
  const members = useMembers(slug ?? "");

  if (!slug) return null;

  const isOwner = ws.data?.role === "owner";
  const myUserId = me.data?.id ?? "";

  return (
    <div className="mx-auto max-w-3xl p-8">
      <PageHeader
        title={t("members.title")}
        description={t("members.description")}
        actions={
          isOwner ? (
            <Button variant="primary" onClick={() => setInviteOpen(true)}>
              {t("members.invite")}
            </Button>
          ) : undefined
        }
      />

      {/* Error state */}
      {members.isError && (
        <Banner kind="error" className="mb-4">
          {apiErrorMessage(members.error, tErr)}
        </Banner>
      )}

      {/* Loading */}
      {members.isLoading && (
        <>
          <span className="sr-only">{t("members.loading")}</span>
          <SkeletonRows rows={3} />
        </>
      )}

      {/* Members table */}
      {members.data && (
        <MembersTable
          slug={slug}
          members={members.data}
          myUserId={myUserId}
          isOwner={isOwner}
        />
      )}

      {/* Pending invites */}
      <PendingInvites slug={slug} isOwner={isOwner} />

      {/* Invite modal */}
      <InviteModal
        slug={slug}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
    </div>
  );
}
